import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import Script from 'next/script';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useVertical } from 'lib/Hooks/useVertical';
import { launchAuthenticationModal } from 'lib/authenticationUtils';
import { PIANO } from 'lib/brandFeatures';
import { getCookie } from 'lib/cookies';
import { useBedrockRegistration } from 'store';
import { PIANO_URI, getPianoAid, PIANO_ENV } from 'lib/pianoConfig';
import PianoWrapper from './PianoWrapper';

// constants
const LD_FLAG = 'piano-today';
const TOKEN_POLL_INTERVAL = 5_000; // in case the cookie is not updated in zustand

const PianoContext = createContext(null);

/**
 * Custom hook to access the PianoContext.
 * @returns {any} The current value of the PianoContext.
 * @throws {Error} If used outside of a PianoContext provider.
 */
export const usePiano = () => useContext(PianoContext);

/**
 * Retrieves the access token from cookies if the code is running in a browser environment.
 * @returns {string|null} The access token if available, or null if running in a non-browser environment.
 */
const getAccessToken = () => (typeof window !== 'undefined' ? getCookie('access_token') : null);

/**
 * PianoProvider injects the Piano JS SDK and keeps its auth state in‑sync
 * with Bedrock (both the zustand store and the auth cookie).
 *
 * @param {object}   props
 * @param {ReactNode} props.children  React subtree to wrap
 * @returns {JSX.Element}
 */
// eslint-disable-next-line import/no-default-export
export default function PianoProvider({ children }) {
  const { vertical } = useVertical();
  const flags = useFeatureFlagContext();
  const pianoEnabled = flags?.[LD_FLAG] && getFeatureConfigForBrand(PIANO, vertical);

  const brandAid = useMemo(() => getPianoAid(PIANO_ENV, vertical), [vertical]);
  const scriptSrc = brandAid ? `${PIANO_URI}/xbuilder/experience/load?aid=${brandAid}` : null;

  // sync Piano with Auth State
  const syncAuthState = useCallback(
    (accessToken) => {
      if (!pianoEnabled) return;

      PianoWrapper.setUserReference(accessToken || null);
      if (accessToken) {
        PianoWrapper.tpPush(['init']);
      }
      PianoWrapper.reloadGate();

      // UAT‑only helper — create a target <div> for Composer templates
      if (
        typeof window === 'undefined'
        || window.location.hostname !== 'uat-ramen2.dev.today.com'
        || !window.location.pathname.startsWith('/piano-sandbox')
      ) return;

      const gateClass = accessToken
        ? 'piano-gate-authenticated'
        : 'piano-gate-unauthenticated';

      document
        .querySelector(
          '.piano-gate-authenticated, .piano-gate-unauthenticated',
        )
        ?.remove();

      const marker = document.createElement('div');
      marker.className = gateClass;
      document.body.prepend(marker);
    },
    [pianoEnabled],
  );

  // Instant propagation from zustand (Bedrock store)
  const bedrockStoreToken = useBedrockRegistration((s) => s.access_token);
  useEffect(() => {
    if (pianoEnabled) syncAuthState(bedrockStoreToken);
  }, [pianoEnabled, bedrockStoreToken, syncAuthState]);

  const initialAccessToken = getAccessToken();
  useEffect(() => {
    if (pianoEnabled) syncAuthState(initialAccessToken);
  }, [pianoEnabled, syncAuthState, initialAccessToken]);

  // Periodic cookie poll – catches silent refreshes that didn’t hit zustand - to cover for old logic pages
  const accessTokenRef = useRef(initialAccessToken);
  useEffect(() => {
    if (!pianoEnabled) return undefined;

    const id = setInterval(() => {
      const current = getAccessToken();
      if (current !== accessTokenRef.current) {
        accessTokenRef.current = current;
        syncAuthState(current);
      }
    }, TOKEN_POLL_INTERVAL);

    return () => clearInterval(id);
  }, [pianoEnabled, syncAuthState]);

  // Global Bedrock launcher for Piano experiences
  const authenticate = useCallback(() => {
    const openModal = launchAuthenticationModal();
    if (typeof openModal === 'function') openModal();
  }, []);

  useEffect(() => {
    if (!pianoEnabled) return;
    window.bedrock = window.bedrock || {};
    window.bedrock.authenticate = authenticate;
  }, [pianoEnabled, authenticate]);

  const api = useMemo(
    () => Object.freeze({
      pianoEnabled,
      push: PianoWrapper.tpPush,
      setUserRef: PianoWrapper.setUserReference,
      // eslint-disable-next-line jsdoc/require-jsdoc
      trackEvent: (name, props = {}) => PianoWrapper.tpPush(['track', { name, props }]),
      authenticate,
    }),
    [pianoEnabled, authenticate],
  );

  return (
    <>
      {pianoEnabled && scriptSrc && (
        <>
          <Script
            id="piano-stub"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{ __html: 'window.tp = window.tp || [];' }}
          />
          <Script
            id="piano-runtime"
            strategy="beforeInteractive"
            src={scriptSrc}
          />
        </>
      )}

      <PianoContext.Provider value={api}>{children}</PianoContext.Provider>
    </>
  );
}

export const { tpPush } = PianoWrapper;
